/**
 * The widget's ready-made looks, as data.
 *
 * Kept apart from styles.ts because the operator console imports this file for
 * its gallery and live preview: what a site owner picks from is, by
 * construction, the same list the widget knows how to draw. The server keeps
 * its own copy of the ids (lib/chat-db widgetConfig.ts) and a test holds the
 * two equal.
 */

export const PRESET_IDS = [
  // Aurora leads: it is AgentlyDesk's own look, and the marketing site's theme
  // gallery opens on it, so the console should not bury it at the end.
  "aurora",
  "classic",
  "paper",
  "neon",
  "glass",
  "bubble",
  "terminal",
  "corporate",
  "editorial",
  "brutal",
  "dock",
  "forge",
] as const;

export type PresetId = (typeof PRESET_IDS)[number];
export type FontChoice = "preset" | "system" | "rounded" | "serif" | "mono";
export type LauncherStyle = "preset" | "circle" | "square" | "pill";
/** What the launcher actually is once the preset and the override are combined. */
export type LauncherShape = "circle" | "square" | "pill" | "tab" | "chamfer";

export interface PresetInfo {
  /** Colours the preset was designed around; the panel offers to apply them. */
  primary: string;
  onPrimary: string;
  launcher: LauncherShape;
}

export const PRESETS: Record<PresetId, PresetInfo> = {
  aurora: { primary: "#ffc23d", onPrimary: "#16203a", launcher: "circle" },
  classic: { primary: "#2563eb", onPrimary: "#ffffff", launcher: "circle" },
  paper: { primary: "#3f3f46", onPrimary: "#ffffff", launcher: "circle" },
  neon: { primary: "#22d3ee", onPrimary: "#04161c", launcher: "chamfer" },
  glass: { primary: "#4f46e5", onPrimary: "#ffffff", launcher: "circle" },
  bubble: { primary: "#db2777", onPrimary: "#ffffff", launcher: "circle" },
  terminal: { primary: "#d79921", onPrimary: "#1d2021", launcher: "square" },
  corporate: { primary: "#1e3a8a", onPrimary: "#ffffff", launcher: "square" },
  editorial: { primary: "#8b1e3f", onPrimary: "#ffffff", launcher: "circle" },
  brutal: { primary: "#ffd60a", onPrimary: "#000000", launcher: "pill" },
  dock: { primary: "#0f766e", onPrimary: "#ffffff", launcher: "tab" },
  forge: { primary: "#4db8ff", onPrimary: "#04121f", launcher: "chamfer" },
};

/** The fields this file reads; every one is optional because older servers do not send them. */
export interface LookConfig {
  preset?: string;
  launcherStyle?: string;
}

/** An unknown or missing preset is classic — a newer server's name must not break an older widget. */
export function presetOf(config: LookConfig): PresetId {
  return (PRESET_IDS as readonly string[]).includes(config.preset ?? "") ? (config.preset as PresetId) : "classic";
}

export function launcherShape(config: LookConfig): LauncherShape {
  const style = config.launcherStyle;
  if (style === "circle" || style === "square" || style === "pill") return style;
  return PRESETS[presetOf(config)].launcher;
}

/**
 * How long the panel stays on the page after it is dismissed, so that a preset
 * with an exit animation can play it (CSS cannot animate a node that is no
 * longer in the DOM).
 *
 * Zero for every other preset, and that is not a nicety: a lingering panel is
 * still a `.panel` element, and `.panel ~ .launcher` rules act on it — the
 * phone layout hides the launcher while a panel is up, and dock slides its tab
 * aside. A beat they do not use would blink the launcher out for a quarter of
 * a second on every close.
 */
export function exitBeatMs(config: LookConfig): number {
  return presetOf(config) === "aurora" ? 240 : 0;
}

/** Pill and tab launchers carry words; the round and square ones are an icon only. */
export function launcherHasText(config: LookConfig): boolean {
  const shape = launcherShape(config);
  return shape === "pill" || shape === "tab";
}
